/**
 * PC Build Model
 *
 * Complete PC build configuration with compatibility validation
 */

import type { CPUModel } from './cpu/cpu-model'
import type { MotherboardModel } from './motherboard/motherboard-model'
import type { ComponentItem } from '../components/shared/ComponentSelectionModal'

// Extend existing models to include necessary compatibility data
// Types support BOTH list-level flat fields AND detail-level nested fields.
// At runtime, list data provides flat fields; detail data adds nested fields.
export interface GPUModel extends ComponentItem {
  series: string
  architecture: string
  // Flat fields (always available from list data)
  tdp?: number                    // TGP in watts
  length?: number                 // mm
  width?: number                  // mm
  height?: number                 // mm
  slots?: number                  // PCIe slot count
  pciePowerConnectors?: string[]  // e.g., ["8-pin", "6-pin"]
  boostClock?: number             // MHz
  memorySize?: number             // GB
  memoryType?: string             // e.g., "GDDR6X"
  chipName?: string               // e.g., "AD102"
  // Nested fields (detail data only)
  physicalSpecs?: {
    length: number
    width: number
    height: number
    slots: number
    weight: number
  }
  powerAndThermal?: {
    totalGraphicsPower: number
    recommendedPsu: number
    auxiliaryPower: {
      connectors: string[]
      totalConnectors: number
    }
  }
  pciExpress?: {
    version: string
    lanes: number
    formFactor: string
  }
}

export interface MemoryModel extends ComponentItem {
  type: 'DDR4' | 'DDR5'
  speed: number                 // MHz
  voltage: number               // e.g., 1.35
  formFactor: 'DIMM' | 'SO-DIMM'
  // Flat fields (from list data)
  totalCapacity?: number        // total GB
  moduleCount?: number          // modules in kit
  rgb?: boolean                 // RGB lighting
  // Detail-only fields
  capacity?: number             // GB per module
  timings?: string              // e.g., "16-18-18-38"
  height?: number               // mm (for cooler clearance)
  kit?: {
    moduleCount: number
    totalCapacity: number
  }
}

export interface StorageModel extends ComponentItem {
  capacity: number              // GB
  interface: string             // e.g., "PCIe 4.0 x4", "SATA 6Gb/s"
  formFactor?: string           // e.g., "M.2 2280", "2.5", "3.5"
  // Flat fields (from list data — uses 'category' not 'type')
  category?: string             // 'NVMe SSD', 'SATA SSD', 'HDD'
  sequentialRead?: number       // MB/s
  sequentialWrite?: number      // MB/s
  // Legacy/detail fields
  type?: 'NVMe' | 'SATA_SSD' | 'HDD'
  sequential?: {
    read: number
    write: number
  }
  endurance?: number            // TBW for SSDs
}

export interface PSUModel extends ComponentItem {
  wattage: number
  efficiency: string            // e.g., "80 PLUS Gold"
  modular: 'Full' | 'Semi' | 'Non-modular'
  // Flat fields (from list data)
  formFactor?: string           // 'ATX', 'SFX', etc.
  // Detail-only fields
  standard?: 'ATX' | 'SFX' | 'SFX-L'
  connectors?: {
    motherboard: string
    cpu: string[]
    pcie: string[]
    sata: number
    molex: number
  }
  dimensions?: {
    length: number
    width: number
    height: number
  }
  protections?: {
    ocp: boolean
    ovp: boolean
    uvp: boolean
    otp: boolean
    scp: boolean
  }
}

export interface CaseModel extends ComponentItem {
  formFactor: string            // e.g., "Mid Tower"
  // Flat fields (from list data)
  motherboardSupport?: string[]  // ["ATX", "Micro-ATX", "Mini-ITX"]
  maxGPULength?: number          // mm
  maxCpuCoolerHeight?: number    // mm
  psuSupport?: string[]          // ["ATX", "SFX"]
  maxPsuLength?: number          // mm
  pciSlots?: number
  volume?: number                // liters
  // Detail-only nested fields
  compatibility?: {
    motherboardFormFactors: string[]
    psuFormFactors: string[]
    maxGraphicsCardLength: number
    maxCpuCoolerHeight: number
    maxPsuLength: number
  }
  expansionSlots?: {
    pciSlots: number
    halfHeightSlots: number
    storageSlots: {
      m2: number
      sata25: number
      sata35: number
    }
  }
  dimensions?: {
    width: number
    height: number
    depth: number
  }
}

export interface CPUCoolerModel extends ComponentItem {
  type: 'Air Tower' | 'AIO Liquid' | 'Low Profile'
  // Flat fields (from list data)
  socketCompatibility?: string[]   // ["LGA1700", "AM5", etc.]
  tdp?: number                     // max TDP supported in watts
  height?: number                  // mm
  fanSize?: number | null
  radiatorSize?: number | null
  noiseLevelLoad?: number | null
  hasRgbLighting?: boolean
  // Detail-only nested fields
  compatibility?: {
    sockets: string[]
    maxTdp: number
    ramClearance: number
    caseClearance: number
  }
  dimensions?: {
    height: number
    width: number
    depth: number
    weight: number
  }
  performance?: {
    maxTdp: number
    noiseLevel: {
      idle: number
      load: number
    }
  }
}

// Main PC Build interface
export interface PCBuild {
  id: string
  name: string
  createdAt: Date
  updatedAt: Date

  // Components
  cpu?: CPUModel
  motherboard?: MotherboardModel
  gpu?: GPUModel[]              // Support multiple GPUs
  memory?: MemoryModel[]        // Support multiple memory modules
  storage?: StorageModel[]      // Support multiple storage devices
  psu?: PSUModel
  case?: CaseModel
  cpuCooler?: CPUCoolerModel

  // Build metadata
  estimatedPrice?: number
  compatibility?: CompatibilityResult
}

// Compatibility validation types
export enum CompatibilityLevel {
  CRITICAL = 'critical',    // 🔴 Build will not work
  WARNING = 'warning',      // 🟡 Not recommended but might work
  INFO = 'info'            // 🔵 Informational
}

export interface CompatibilityIssue {
  id: string
  level: CompatibilityLevel
  category: string          // e.g., "Power", "Physical", "Socket"
  title: string
  description: string
  affectedComponents: string[]  // component IDs
  suggestions?: string[]
}

export interface CompatibilityResult {
  isValid: boolean          // No critical issues
  score: number            // 0-100, higher is better
  issues: CompatibilityIssue[]
  powerConsumption: {
    estimated: number       // watts
    recommended: number     // recommended PSU wattage
    efficiency: number      // 0-1, how close to PSU limit
    breakdown: {
      cpu: number
      gpu: number
      motherboard: number
      memory: number
      storage: number
      cooler: number
    }
  }
  completeness: {
    percentage: number      // 0-100
    missingComponents: string[]
    requiredComponents: string[]
  }
}

// Component type enum for universal handling
export enum ComponentType {
  CPU = 'cpu',
  MOTHERBOARD = 'motherboard',
  GPU = 'gpu',
  MEMORY = 'memory',
  STORAGE = 'storage',
  PSU = 'psu',
  CASE = 'case',
  CPU_COOLER = 'cpuCooler'
}

export interface ComponentTypeConfig {
  type: ComponentType
  name: string
  icon: string
  color: 'indigo' | 'green' | 'blue' | 'orange' | 'purple' | 'red'
  required: boolean         // Is this component required for basic functionality?
  multiple: boolean         // Can have multiple instances?
  maxCount?: number         // Maximum instances if multiple
}

// Component configurations for UI
export const COMPONENT_CONFIGS: Record<ComponentType, ComponentTypeConfig> = {
  [ComponentType.CPU]: {
    type: ComponentType.CPU,
    name: 'Processor',
    icon: 'CpuChipIcon',
    color: 'indigo',
    required: true,
    multiple: false
  },
  [ComponentType.MOTHERBOARD]: {
    type: ComponentType.MOTHERBOARD,
    name: 'Motherboard',
    icon: 'CircuitBoardIcon',
    color: 'green',
    required: true,
    multiple: false
  },
  [ComponentType.GPU]: {
    type: ComponentType.GPU,
    name: 'Graphics Card',
    icon: 'ComputerDesktopIcon',
    color: 'blue',
    required: false,
    multiple: true,
    maxCount: 4
  },
  [ComponentType.MEMORY]: {
    type: ComponentType.MEMORY,
    name: 'Memory',
    icon: 'ChartBarIcon',
    color: 'purple',
    required: true,
    multiple: true,
    maxCount: 8
  },
  [ComponentType.STORAGE]: {
    type: ComponentType.STORAGE,
    name: 'Storage',
    icon: 'CircleStackIcon',
    color: 'orange',
    required: true,
    multiple: true,
    maxCount: 10
  },
  [ComponentType.PSU]: {
    type: ComponentType.PSU,
    name: 'Power Supply',
    icon: 'BoltIcon',
    color: 'red',
    required: true,
    multiple: false
  },
  [ComponentType.CASE]: {
    type: ComponentType.CASE,
    name: 'Case',
    icon: 'BuildingOfficeIcon',
    color: 'purple',
    required: true,
    multiple: false
  },
  [ComponentType.CPU_COOLER]: {
    type: ComponentType.CPU_COOLER,
    name: 'CPU Cooler',
    icon: 'FanIcon',
    color: 'blue',
    required: true,
    multiple: false
  }
}